import React, { useContext, useRef, useState } from 'react'

import classNames from 'classnames'
import Image from 'next/image'

import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Fade from '@mui/material/Fade'
import Link from '@mui/material/Link'
import Popper from '@mui/material/Popper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import useTrans from '@/hooks/useTrans'

import { IconName, staticImportIcon } from '@/utils/static-import-icon'

import MainMenuItems from '@/layouts/MainLayout/components/MainMenuItems'
import ExtendedMenuItems
  from '@/layouts/MainLayout/components/ExtendedMenuItems'
import { PendingTransactionsCtx } from '@/contexts/pending-transactions'

const FooterMobile = () => {
  const trans = useTrans()
  const [anchorEl, setAnchorEl] = useState<any>(null)
  const ref = useRef()
  const open = Boolean(anchorEl)
  const { isDisplayPendingBadge, pendingTransactions } = useContext(PendingTransactionsCtx)

  const handleClick = () => {
    setAnchorEl(anchorEl ? null : ref.current)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Box className="w-full fixed z-10 bottom-[8px]" ref={ref}>
      <Container>
        <ClickAwayListener onClickAway={handleClose}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className="bg-[#121823] rounded-[28px] p-2"
          >
            <MainMenuItems />
            <Stack>
              <Box className="relative">
                {isDisplayPendingBadge && (
                  <span className="absolute h-4 min-w-[16px] bg-[#FF6701] rounded-full font-bold text-center text-xs pointer-events-none px-[3px] top-[-8px] left-[50%] translate-x-[-50%]">
                    {pendingTransactions.length}
                  </span>
                )}
                <MoreHorizIcon
                  data-testid="btn-extra-menu"
                  onClick={handleClick}
                  className={classNames(
                    'sm:block cursor-pointer opacity-80 hover:opacity-100',
                    open && 'opacity-100',
                  )}
                />
              </Box>
            </Stack>
            <Popper open={open} anchorEl={ref.current} placement="top" className="z-10 w-full px-4">
              <Box className="bg-[#1E2735] rounded-[16px] border border-[#61667E] p-2">
                <ExtendedMenuItems handleClose={handleClose} />
                <Stack
                  data-testid="social-links-wrapper"
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  spacing={3}
                  className="px-4 py-4"
                >
                  <Stack>
                    <Tooltip
                      title={IconName.DISCORD_MOBILE}
                      placement="top"
                      TransitionComponent={Fade}
                    >
                      <Link
                        data-testid="social-link-discord"
                        href="https://discord.gg/JNvaFP5QCz"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Image
                          src={staticImportIcon(IconName.DISCORD_MOBILE)}
                          alt={IconName.DISCORD_MOBILE}
                          width={24}
                        />
                      </Link>
                    </Tooltip>
                  </Stack>
                  <Stack>
                    <Tooltip
                      title={IconName.REDDIT_MOBILE}
                      placement="top"
                      TransitionComponent={Fade}
                    >
                      <Link
                        data-testid="social-link-reddit"
                        href="https://www.reddit.com/r/renecblockchain/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Image
                          src={staticImportIcon(IconName.REDDIT_MOBILE)}
                          alt={IconName.REDDIT_MOBILE}
                          width={24}
                        />
                      </Link>
                    </Tooltip>
                  </Stack>
                  <Stack>
                    <Tooltip
                      title={IconName.TWITTER_MOBILE}
                      placement="top"
                      TransitionComponent={Fade}
                    >
                      <Link
                        data-testid="social-link-twitter"
                        href="https://twitter.com/NemoSwapDEX"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Image
                          src={staticImportIcon(IconName.TWITTER_MOBILE)}
                          alt={IconName.TWITTER_MOBILE}
                          width={24}
                        />
                      </Link>
                    </Tooltip>
                  </Stack>
                  <Stack>
                    <Tooltip
                      title={IconName.TELEGRAM_MOBILE}
                      placement="top"
                      TransitionComponent={Fade}
                    >
                      <Link
                        data-testid="social-link-telegram"
                        href="https://t.me/nemoswapdex"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Image
                          src={staticImportIcon(IconName.TELEGRAM_MOBILE)}
                          alt={IconName.TELEGRAM_MOBILE}
                          width={24}
                        />
                      </Link>
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack className="px-2 py-2">
                  <Divider />
                </Stack>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  className="py-2"
                >
                  <Typography data-testid="footer-copyright" variant="body2" color="textSecondary" textAlign="center">
                    © 2023 Renec Foundation. All rights reserved.
                  </Typography>
                  <Link href="/terms-and-conditions" variant="body2" color="textSecondary">
                    {trans.terms_of_use}
                  </Link>
                </Stack>
              </Box>
            </Popper>
          </Stack>
        </ClickAwayListener>
      </Container>
    </Box>
  )
}

export default FooterMobile
